/**
 * Shared detector + copy for the blog → /diagnose cross-link.
 *
 * The July 2026 post-Hoggie PostHog audit found the diagnosis gate converts ~25%
 * of viewers to email/sign-in (vs ~1-3% for passive capture surfaces), but only
 * a trickle of blog traffic reaches it — every existing blog CTA points at the
 * grass-ID flow (/#analysis-card) or a /tools/* calculator, never /diagnose.
 *
 * "Problem" posts (disease, fungus, pest damage, discoloration) are the natural
 * feeder: the reader has a symptom and no diagnosis, which is exactly what the
 * photo-based /diagnose flow resolves. This module centralizes that routing so
 * the inline CTA, sidebar CTA, and post-read card all agree on which posts get
 * the diagnose ask.
 *
 * Deliberately excludes grub posts — they already have a dedicated, more
 * actionable /tools/grub-control-calculator branch in contextual-tool-cta, and
 * weed/fertilizer/watering posts keep their existing calculator CTAs.
 */

import { isGrassIdentityIntent } from '@/lib/blog-intent'

// Matched against slug + category + tags, lowercased with hyphens normalized to
// spaces so "brown-patch" (slug) and "Brown Patch" (category) both hit. Scoped to
// symptom/identification language, not general care tasks.
const DIAGNOSE_KEYWORDS = [
  'disease',
  'fungus',
  'fungal',
  'blight',
  'rust',
  'mold',
  'mildew',
  'brown patch',
  'brown spot',
  'dead spot',
  'dead patch',
  'dollar spot',
  'leaf spot',
  'red thread',
  'fairy ring',
  'pythium',
  'pest',
  'insect',
  'chinch',
  'armyworm',
  'webworm',
  'billbug',
  'mole cricket',
  'cutworm',
  'aphid',
  'problem',
  'diagnos',
  'whats wrong',
  'what is wrong',
  'why is my',
  // Symptom stems surfaced by the detector-coverage audit
  // (scripts/audit-diagnose-detector-coverage.ts). Deliberately narrow: bare
  // 'yellow'/'yellowing' over-matches fertilizer-product tags, so only the
  // explicit symptom phrasings are included. All four are ungrouped generic
  // stems on purpose - they must yield to grass-identity intent.
  'turning yellow',
  'salt damage',
  'overfertiliz',
  'underwatering',
]

/**
 * The most specific diagnose keyword a post matches, or null when it matches
 * none.
 *
 * Shared matching core for both `isDiagnosisPost` (boolean API, unchanged) and
 * the symptom-keyed copy map used by the inline diagnose picker (V2). Returns
 * the matched keyword so callers can key symptom-specific headings + the
 * `symptom_key` analytics property off it. Same grub exclusion + word-boundary
 * rules as before. When several keywords match (a brown-patch post usually also
 * contains 'disease'), the winner is the one in the EARLIEST DIAGNOSE_COPY_MAP
 * group, because the map is ordered specific-to-generic; keywords outside any
 * group (generic problem stems) rank last. Codex review catch 2026-07-24:
 * plain first-match-in-array returned 'disease' for brown-patch slugs and
 * shadowed every specific heading.
 */
export function matchedDiagnoseKeyword(
  slug?: string,
  category?: string,
  tags?: string[],
): string | null {
  const hay = [slug, category, ...(tags ?? [])]
    .filter(Boolean)
    .join(' ')
    .toLowerCase()
    .replace(/-/g, ' ')
  // Grub posts are excluded per the module doc above — they have a dedicated
  // /tools/grub-control-calculator branch. The inline CTA already preempts them
  // (contextual-tool-cta matches grub BEFORE calling this detector), but the
  // sticky sidebar and post-read card call this directly, so without this guard
  // a grub post's 'pest' category routed those two surfaces to /diagnose.
  // Scoped to the SLUG only: a grub TAG on an otherwise diagnose-worthy post
  // (e.g. a yellowing-lawn differential that mentions grubs) must not suppress
  // the surfaces — posts ABOUT grubs name them in the slug and stay excluded.
  if (/\bgrub/.test((slug ?? '').toLowerCase().replace(/-/g, ' '))) return null
  // Leading word-boundary (not raw substring) so 'rust' matches 'rust'/'rusty'
  // but NOT 'trust'/'crust', while stems like 'diagnos' still catch
  // 'diagnose'/'diagnosis'. Keywords contain no regex metacharacters.
  const matches = DIAGNOSE_KEYWORDS.filter((k) => new RegExp(`\\b${k}`).test(hay))
  if (matches.length === 0) return null
  const groupRank = (k: string): number => {
    const i = DIAGNOSE_COPY_MAP.findIndex((g) => g.keywords.includes(k))
    return i === -1 ? DIAGNOSE_COPY_MAP.length : i
  }
  return matches.reduce((best, k) => (groupRank(k) < groupRank(best) ? k : best))
}

/**
 * True when `keyword` is a generic problem stem — a DIAGNOSE_KEYWORDS entry that
 * belongs to no DIAGNOSE_COPY_MAP group, so it names no actual symptom. These
 * stems say "this post is about something going wrong" and nothing more, which
 * is weak enough that an identity-intent post must not lose to them (see
 * `isDiagnosisPost`).
 */
export function isGenericDiagnoseKeyword(keyword: string | null): boolean {
  if (!keyword) return false
  return !DIAGNOSE_COPY_MAP.some((g) => g.keywords.includes(keyword))
}

/**
 * True when a blog post is a symptom/problem-solving piece that should route to
 * the photo-based /diagnose flow rather than a calculator or the grass-ID flow.
 *
 * Generic stems yield to grass-identity intent. Measured 2026-07-31: the
 * visual-identification guide (category "Grass Types", tag "lawn-diagnostics")
 * matched the 'diagnos' stem via that tag and served 487 impressions of "What's
 * wrong with your lawn?" to readers who wanted their grass type. A SPECIFIC
 * symptom match still wins over identity intent, so "st augustine brown patch"
 * stays on diagnose. All four routing surfaces (inline CTA, sidebar, post-read
 * card, early-CTA marker) read this, so the rule lives here rather than in any
 * one of them.
 */
export function isDiagnosisPost(
  slug?: string,
  category?: string,
  tags?: string[],
): boolean {
  const keyword = matchedDiagnoseKeyword(slug, category, tags)
  if (keyword === null) return false
  if (isGenericDiagnoseKeyword(keyword) && isGrassIdentityIntent(slug, category)) {
    return false
  }
  return true
}

/**
 * Symptom-keyed headings for the inline diagnose picker (approved mockup V2).
 * Each group lists the diagnose keywords that share a heading; a matched
 * keyword resolves to its group's heading via `diagnoseHeadingFor`, falling
 * back when the keyword is a generic problem stem ('problem', 'diagnos', etc.).
 * No em-dashes (house style).
 */
export const DIAGNOSE_COPY_MAP: {
  keywords: readonly string[]
  heading: string
}[] = [
  {
    keywords: ['brown patch', 'brown spot', 'dead spot', 'dead patch'],
    heading: "What's causing those brown patches?",
  },
  {
    keywords: ['dollar spot', 'leaf spot'],
    heading: "What's making those small tan spots?",
  },
  {
    keywords: ['rust'],
    heading: 'Why is your grass turning orange?',
  },
  {
    keywords: [
      'mold', 'mildew', 'fungus', 'fungal', 'blight', 'pythium',
      'red thread', 'fairy ring', 'disease',
    ],
    heading: 'Is this a lawn disease? Find out from a photo',
  },
  {
    keywords: [
      'chinch', 'armyworm', 'webworm', 'billbug', 'mole cricket',
      'cutworm', 'aphid', 'insect', 'pest',
    ],
    heading: 'Is something eating your lawn?',
  },
]

/** Fallback heading for problem posts that match a generic stem, not a symptom group. */
export const DIAGNOSE_FALLBACK_HEADING = "What's wrong with your lawn?"

/**
 * Synthetic symptom key for weed posts that fall back to /diagnose because the
 * weed identifier's analysis capability is off. Not a DIAGNOSE_KEYWORDS entry
 * and never returned by `matchedDiagnoseKeyword` — the component sets it
 * directly on that branch so the fallback is segmentable in PostHog without
 * renaming cta_location (~200 impressions/week were landing on the generic
 * heading, measured 2026-07-31).
 */
export const WEED_FALLBACK_SYMPTOM_KEY = 'weed_fallback'

/** Heading for the weed identifier -> diagnose fallback. No em-dashes. */
export const DIAGNOSE_WEED_FALLBACK_HEADING = 'What weed is taking over your lawn?'

/** Shared tagline for the inline diagnose picker (V2). No em-dashes. */
export const DIAGNOSE_INLINE_TAGLINE =
  'Snap a photo of the damage right here and get the likely cause plus the fix, free.'

/** Resolve the symptom-keyed heading for a matched diagnose keyword (or the fallback). */
export function diagnoseHeadingFor(keyword: string | null): string {
  if (!keyword) return DIAGNOSE_FALLBACK_HEADING
  if (keyword === WEED_FALLBACK_SYMPTOM_KEY) return DIAGNOSE_WEED_FALLBACK_HEADING
  const group = DIAGNOSE_COPY_MAP.find((g) => g.keywords.includes(keyword))
  return group?.heading ?? DIAGNOSE_FALLBACK_HEADING
}

/** Shared copy so every diagnose entry point reads consistently. */
export const DIAGNOSE_CTA = {
  href: '/diagnose',
  label: 'Diagnose your lawn',
  tagline: 'Not sure what is wrong? Get a likely cause and fix from a photo.',
  /** cta_clicked location values, per surface, so PostHog can segment the source. */
  location: {
    inline: 'blog_inline_diagnose',
    sidebar: 'content_sidebar_diagnose',
    postRead: 'blog_post_read_diagnose',
    footer: 'footer_diagnose',
  },
} as const
